import * as React from "react";
import * as Toast from "@radix-ui/react-toast";

import Snackbar, { SnackbarVariant, SnackbarVariantValue } from "./index";
import * as styles from "./Snackbar.css";

const SNACKBAR_DURATION = 8000;

let globalAddSnackbar:
  | ((message: string, variant?: SnackbarVariantValue) => void)
  | null = null;

export const addSnackbar = (
  message: string,
  variant?: SnackbarVariantValue
) => {
  if (globalAddSnackbar) {
    globalAddSnackbar(message, variant);
  }
};

interface SnackbarItem {
  id: string;
  variant: SnackbarVariantValue;
  message: string;
  open: boolean;
}

interface SnackbarRegistryContextValue {
  register: (id: string) => void;
  unregister: (id: string) => void;
  isEarliest: (id: string) => boolean;
  hasMultiple: boolean;
}

interface SnackbarContextValue {
  addSnackbar: (message: string, variant?: SnackbarVariantValue) => void;
}

const SnackbarRegistryContext =
  React.createContext<SnackbarRegistryContextValue | null>(null);

const SnackbarContext = React.createContext<SnackbarContextValue | null>(null);

export const useSnackbarRegistry = () => {
  return React.useContext(SnackbarRegistryContext);
};

export const useSnackbar = () => {
  const context = React.useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};

export interface SnackbarProviderProps {
  children?: React.ReactNode;
  swipeDirection?: "right" | "left" | "up" | "down";
  swipeThreshold?: number;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children,
  swipeDirection = "up",
  swipeThreshold = 50,
}) => {
  const [registeredIds, setRegisteredIds] = React.useState<string[]>([]);
  const registeredIdsRef = React.useRef<string[]>([]);
  const [snackbars, setSnackbars] = React.useState<SnackbarItem[]>([]);
  const idCounter = React.useRef(0);

  // Keep ref in sync with state for stable callback access
  React.useEffect(() => {
    registeredIdsRef.current = registeredIds;
  }, [registeredIds]);

  const register = React.useCallback((id: string) => {
    setRegisteredIds((prev) => {
      if (prev.includes(id)) return prev;
      const newIds = [...prev, id];
      registeredIdsRef.current = newIds;
      return newIds;
    });
  }, []);

  const unregister = React.useCallback((id: string) => {
    setRegisteredIds((prev) => {
      const newIds = prev.filter((existingId) => existingId !== id);
      registeredIdsRef.current = newIds;
      return newIds;
    });
  }, []);

  const isEarliest = React.useCallback((id: string) => {
    const ids = registeredIdsRef.current;
    return ids.length > 0 && ids[0] === id;
  }, []);

  const hasMultiple = registeredIds.length > 1;

  const addSnackbarInternal = React.useCallback(
    (
      message: string,
      variant: SnackbarVariantValue = SnackbarVariant.GUIDANCE
    ) => {
      idCounter.current += 1;
      const id = `snackbar-${idCounter.current}-${Date.now()}`;
      setSnackbars((prev) => [...prev, { id, variant, message, open: true }]);
    },
    []
  );

  React.useEffect(() => {
    globalAddSnackbar = addSnackbarInternal;
    return () => {
      globalAddSnackbar = null;
    };
  }, [addSnackbarInternal]);

  const handleOpenChange = React.useCallback(
    (id: string) => (open: boolean) => {
      if (!open) {
        setSnackbars((prev) =>
          prev.map((s) => (s.id === id ? { ...s, open: false } : s))
        );
        setTimeout(() => {
          setSnackbars((prev) => prev.filter((s) => s.id !== id));
        }, 150);
      }
    },
    []
  );

  const registryContextValue = React.useMemo(
    () => ({ register, unregister, isEarliest, hasMultiple }),
    [register, unregister, isEarliest, hasMultiple]
  );

  const snackbarContextValue = React.useMemo(
    () => ({ addSnackbar: addSnackbarInternal }),
    [addSnackbarInternal]
  );

  return (
    <Toast.Provider
      swipeDirection={swipeDirection}
      swipeThreshold={swipeThreshold}
    >
      <SnackbarContext.Provider value={snackbarContextValue}>
        <SnackbarRegistryContext.Provider value={registryContextValue}>
          {children}
          {snackbars.map((snackbar) => (
            <Snackbar
              key={snackbar.id}
              variant={snackbar.variant}
              message={snackbar.message}
              open={snackbar.open}
              onOpenChange={handleOpenChange(snackbar.id)}
              duration={SNACKBAR_DURATION}
            />
          ))}
        </SnackbarRegistryContext.Provider>
      </SnackbarContext.Provider>
      <Toast.Viewport className={styles.toastViewport} />
    </Toast.Provider>
  );
};
